import { useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useCurrentAgency } from '../features/agencies/api/agenciesApi';

/**
 * Component that loads the agency's theme color and applies it to all users.
 * Should be placed inside AuthProvider and QueryProvider to have access to user and agency data.
 */
const AgencyThemeLoader: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const { setAgencyColor } = useTheme();
  // Only fetch agency data when user is authenticated and has an agency
  const shouldFetchAgency = !!(isAuthenticated && user?.agencyId);
  const { data: agency } = useCurrentAgency(shouldFetchAgency);

  useEffect(() => {
    // Only load agency color for agency-based users (not super admins without agency)
    if (user && agency?.primaryColor) {
      console.log('🎨 AgencyThemeLoader: Loading agency color:', agency.primaryColor);
      setAgencyColor(agency.primaryColor);
    } else if (user && !agency?.primaryColor) {
      // Reset to default if no agency color is set
      console.log('🎨 AgencyThemeLoader: No agency color set, using default');
      setAgencyColor(null);
    }
  }, [user, agency?.primaryColor, setAgencyColor]);

  // Fachbegriffe folgen dem Land der Agentur (DE: Pflegegrad, Krankenkasse, …).
  const { setTermCountry } = useLanguage();
  useEffect(() => {
    setTermCountry(user ? agency?.country : null);
  }, [user, agency?.country, setTermCountry]);

  return <>{children}</>;
};

export default AgencyThemeLoader;
